/**
 * Turning what the camera read into a guest — the pure half of the
 * scanner. A candidate only ever resolves to a reference that is
 * already on the event's guest list (exact, or one character off), so
 * a misread can't check in someone who isn't there.
 */

import { PARTY_MAX } from "@repo/vihangam-yog-shared";

import { CHAPTER } from "../../chapter";

/** This chapter's prefix ("VYA", "VYS"): the refs its desk ever sees. */
const PREFIX = CHAPTER.refPrefix;

/** The eight code characters, in order, with the dashes as printed. */
export const REF_RE = new RegExp(`^${PREFIX}-[A-Z2-9]{4}-[A-Z2-9]{4}$`);
/** Loose shape for finding refs inside OCR text or a URL. */
// Lookarounds keep "VYAUSTRALIA" in our own URL from reading as a ref.
const LOOSE_RE = new RegExp(
  `(?<![A-Z0-9])${PREFIX}[\\s-]*([A-Z0-9]{4})[\\s-]*([A-Z0-9]{4})(?![A-Z0-9])`,
  "g",
);

/** Positions in a party are 1-based, up to the largest party the form takes. */
const PERSON_MAX = PARTY_MAX;

/** A reference read off a pass, and the person it names when it does. */
export interface RefCandidate {
  ref: string;
  /** 1-based position in the party from a per-person pass; null for
   *  the registration's own code or a printed reference. */
  person: number | null;
}

/** The `p` of our own check-in link, when it is a sensible position. */
export function personFromParams(params: URLSearchParams): number | null {
  const raw = params.get("p");
  if (raw === null || !/^\d{1,2}$/.test(raw)) {
    return null;
  }
  const n = Number(raw);
  return n >= 1 && n <= PERSON_MAX ? n : null;
}

/** Every ref-shaped run in `text`, normalised to PREFIX-XXXX-XXXX. */
export function extractRefCandidates(text: string): RefCandidate[] {
  const out: RefCandidate[] = [];
  const push = (ref: string) => {
    if (!out.some((c) => c.ref === ref)) {
      out.push({ ref, person: null });
    }
  };
  // Our own check-in link carries the ref as a query param: trust it first.
  try {
    if (/^https?:\/\//i.test(text.trim())) {
      const params = new URL(text.trim()).searchParams;
      const ref = params.get("ref");
      // A link is exact: take its ref alone rather than also scanning the
      // host ("vyaustralia" would otherwise read as a ref-shaped run).
      if (ref) {
        return [
          { ref: ref.trim().toUpperCase(), person: personFromParams(params) },
        ];
      }
    }
  } catch {
    // not a URL — fall through to text scanning
  }
  const upper = text.toUpperCase();
  for (const m of upper.matchAll(LOOSE_RE)) {
    push(`${PREFIX}-${m[1]}-${m[2]}`);
  }
  return out;
}

/**
 * Characters the alphabet never uses (0/O, 1/I/L) can only be OCR
 * confusions — turn them into wildcards that match anything.
 */
function normaliseConfusables(ref: string): string {
  return ref.replace(/[0OI1L]/g, "?");
}

export type RefMatch =
  | { kind: "exact"; ref: string }
  | { kind: "fuzzy"; ref: string }
  | { kind: "ambiguous"; refs: string[] }
  | { kind: "none" };

/** Hamming distance over the 8 code characters; `?` matches anything. */
function distance(candidate: string, known: string): number {
  let d = 0;
  for (let i = 0; i < candidate.length; i++) {
    const c = candidate[i];
    if (c === "?" || c === known[i]) {
      continue;
    }
    d += 1;
    if (d > 1) {
      return d;
    }
  }
  return d;
}

export function matchRef(
  candidate: string,
  known: readonly string[],
): RefMatch {
  const c = candidate.trim().toUpperCase();
  if (REF_RE.test(c) && known.includes(c)) {
    return { kind: "exact", ref: c };
  }
  const wild = normaliseConfusables(c);
  if (wild.length !== 13) {
    return { kind: "none" };
  }
  const close = known.filter((k) => distance(wild, k) <= 1);
  const only = close[0];
  if (close.length === 1 && only !== undefined) {
    return { kind: "fuzzy", ref: only };
  }
  if (close.length > 1) {
    return { kind: "ambiguous", refs: close };
  }
  return { kind: "none" };
}
